/**
 * A question's length limit, editable in place: the limit reads as a link
 * ("1,200 words max", or "No limit"); clicking it opens a small popover with
 * the number, its unit (words, pages or characters) and "No limit". Saved to
 * mytender the way the web app's length control saves it.
 */
import { type FC, useEffect, useState } from "react";

import { cn } from "@/agents-v2/utils/cn";
import { Input } from "@/design/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/design/ui/popover";

import { type LimitType, formatLimit, limitValueOf } from "../tender/limits";
import type { Question } from "../tender/questionsApi";

const UNITS: { type: LimitType; label: string }[] = [
  { type: "words", label: "Words" },
  { type: "pages", label: "Pages" },
  { type: "characters", label: "Characters" },
];

/** Digits only; pages may take one decimal point (e.g. 2.5). */
const sanitize = (raw: string, unit: LimitType): string => {
  const cleaned = unit === "pages" ? raw.replace(/[^\d.]/g, "").replace(/(\..*)\./g, "$1") : raw.replace(/\D/g, "");
  return /^0\d/.test(cleaned) ? cleaned.replace(/^0+/, "") : cleaned;
};

export const LimitEditor: FC<{
  question: Question;
  onSave: (value: number | null, unit: LimitType) => Promise<void>;
}> = ({ question, onSave }) => {
  const label = formatLimit(question as never);
  const [open, setOpen] = useState(false);
  const [unit, setUnit] = useState<LimitType>(question.limit_type ?? "words");
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seeded when the editor opens, not on every change to the question: the
  // Tender tab now syncs rows from mytender in the background, and a sync
  // landing mid-edit must not overwrite what the writer is typing.
  useEffect(() => {
    if (!open) return;
    const current = limitValueOf(question as never);
    setUnit(question.limit_type ?? "words");
    setValue(current ? String(current) : "");
    setError(null);
  }, [open, question.section_id]);

  const save = async (next: number | null) => {
    setSaving(true);
    setError(null);
    try {
      await onSave(next, unit);
      setOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't save the limit");
    } finally {
      setSaving(false);
    }
  };
  const number = Number(value);
  const valid = value !== "" && Number.isFinite(number) && number > 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title="Change the length limit"
          className="rounded-xs underline decoration-line-strong decoration-dotted underline-offset-2 hover:text-fg hover:decoration-fg-3"
        >
          {label ? `${label} max` : "No limit"}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={6} className="w-64 p-3">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (valid) void save(number);
          }}
        >
          <div className="text-xs font-semibold text-fg-3">Length limit</div>
          <div className="mt-2 flex rounded-md bg-subtle p-0.5">
            {UNITS.map((u) => (
              <button
                key={u.type}
                type="button"
                onClick={() => {
                  setUnit(u.type);
                  setValue((v) => sanitize(v, u.type));
                }}
                className={cn(
                  "flex-1 rounded-sm py-1 text-xs font-semibold transition-colors duration-fast",
                  unit === u.type ? "bg-surface text-fg shadow-sm" : "text-fg-3 hover:text-fg"
                )}
              >
                {u.label}
              </button>
            ))}
          </div>
          <div className="mt-2 flex items-center gap-2">
            <Input
              id={`limit-${question.section_id}`}
              autoFocus
              inputMode={unit === "pages" ? "decimal" : "numeric"}
              value={value}
              onChange={(e) => setValue(sanitize(e.target.value, unit))}
              placeholder={unit === "pages" ? "e.g. 2" : unit === "characters" ? "e.g. 3000" : "e.g. 500"}
              className="h-8 flex-1"
            />
            <span className="text-xs text-fg-3">max</span>
          </div>
          {error && <p className="mt-1.5 text-xs text-danger">{error}</p>}
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              disabled={saving || !label}
              onClick={() => void save(null)}
              className="text-xs font-semibold text-fg-3 hover:text-fg disabled:opacity-40"
            >
              No limit
            </button>
            <span className="flex-1" />
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-sm px-2 py-1 text-xs font-semibold text-fg-2 hover:bg-subtle"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !valid}
              className="rounded-sm bg-fg px-2.5 py-1 text-xs font-semibold text-fg-inverse disabled:opacity-40"
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  );
};
